import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";
import { LogoMark } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

export default function AdminResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });
      setDone(true);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-[68px] min-h-[80vh] flex items-center justify-center px-4">
      <motion.div
        data-testid="reset-password-card"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-white border border-[#E5E7EB] rounded-[2rem] p-8 sm:p-10 shadow-xl"
      >
        <div className="flex items-center gap-3 mb-2">
          <LogoMark size={36} className="rounded-xl" />
          <h1 className="font-display font-semibold text-2xl">
            Choose a new password
          </h1>
        </div>
        {done ? (
          <div data-testid="reset-password-success" className="text-center py-4">
            <CheckCircle2 size={52} className="text-[#2563EB] mx-auto my-5" strokeWidth={1.5} />
            <p className="text-[#52525B]">Your password has been updated.</p>
            <Link
              to="/admin/login"
              data-testid="reset-login-link"
              className="mt-6 inline-block bg-[#111827] text-[#FFFFFF] font-bold px-8 py-3 rounded-full hover:bg-[#1F2937] transition-colors"
            >
              Sign in
            </Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-[#71717a] mb-7">
              At least 8 characters.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <input
                data-testid="reset-password-input"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-xl px-4 py-3 focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/30"
                placeholder="New password"
              />
              <input
                data-testid="reset-confirm-input"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-xl px-4 py-3 focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/30"
                placeholder="Confirm new password"
              />
              {error && (
                <p data-testid="reset-error" className="text-sm font-semibold text-red-600">
                  {error}
                </p>
              )}
              <button
                data-testid="reset-submit-button"
                type="submit"
                disabled={loading || !token}
                className="w-full bg-[#111827] text-[#FFFFFF] font-bold py-3.5 rounded-full hover:bg-[#1F2937] transition-colors disabled:opacity-60"
              >
                {loading ? "Updating..." : "Update password"}
              </button>
              {!token && (
                <p className="text-sm text-red-600 font-semibold">
                  This reset link is missing its token. Request a new one from
                  the sign-in page.
                </p>
              )}
            </form>
          </>
        )}
      </motion.div>
    </main>
  );
}
